import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { AuditKeyMatter } from '../../../models/auditKeyMatter';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-key-matter-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-key-matter-list.component.html',
  styleUrl: './audit-key-matter-list.component.scss'
})
export class AuditKeyMatterListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditKeyMatters = signal<AuditKeyMatter[]>([]);
  rightItem: AuditKeyMatter | undefined = undefined;
  matchedItems: AuditKeyMatter[] | undefined = undefined;
  isSearchVisible = false;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  auditKeyMatterColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditKeyMatters();
  }

  loadAuditKeyMatters(): void {
    this.spinnerService.show();

    const hasSearchFilters = this.searchFilter.name || this.searchFilter.exactMatch;

    if (hasSearchFilters) {
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchAuditKeyMatters(searchRequest).subscribe({
        next: (response) => {
          this.auditKeyMatters.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading audit key matters:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Audit Key Matters:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      this.lutService.getAuditKeyMattersPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.auditKeyMatters.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading audit key matters:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Audit Key Matters:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadAuditKeyMatters();
  }

  onSortChange(sort: Sort): void {
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadAuditKeyMatters();
  }

  onCreateAuditKeyMatter(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => {
      if (typeof value === 'boolean') {
        return value === true;
      }
      return value !== undefined && value !== null && value !== '';
    }).length;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1;

    if (this.lookupTable?.['paginator']) {
      this.lookupTable['paginator'].pageIndex = 0;
    }

    this.loadAuditKeyMatters();
  }

  onItemSelected(auditKeyMatter: AuditKeyMatter): void {
  }

  onEditAuditKeyMatter(auditKeyMatter: AuditKeyMatter): void {
  }

  onDeleteAuditKeyMatter(auditKeyMatter: AuditKeyMatter): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditKeyMatter.name)
      .subscribe(result => {
        if (result) {
          const originalAuditKeyMatters = this.auditKeyMatters();

          this.auditKeyMatters.update(auditKeyMatters => auditKeyMatters.filter(akm => akm.id !== auditKeyMatter.id));

          this.spinnerService.show();
          this.lutService.deleteAuditKeyMatter(auditKeyMatter.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit key matter deleted successfully');
              this.loadAuditKeyMatters();
            },
            error: (error) => {
              console.error('Error deleting audit key matter:', error);
              this.spinnerService.hide();
              this.auditKeyMatters.set(originalAuditKeyMatters);
              this.toast.error(`<strong>Failed to delete Audit Key Matter:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditKeyMatter, newName: string} | {item: AuditKeyMatter, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: AuditKeyMatter;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.auditKeyMatters.update(auditKeyMatters =>
      auditKeyMatters.map(akm => akm.id === event.item.id ? updatedItem : akm)
    );

    this.spinnerService.show();
    this.lutService.updateAuditKeyMatter(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit key matter updated successfully');
        this.auditKeyMatters.update(auditKeyMatters =>
          auditKeyMatters.map(akm => akm.id === event.item.id ? response : akm)
        );
      },
      error: (error) => {
        console.error('Error updating audit key matter:', error);
        this.spinnerService.hide();
        this.auditKeyMatters.update(auditKeyMatters =>
          auditKeyMatters.map(akm => akm.id === event.item.id ? originalItem : akm)
        );
        this.toast.error(`<strong>Failed to update Audit Key Matter:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newAuditKeyMatter: AuditKeyMatter = { id: tempId, name: event['name'], systemReserved: false };

    this.auditKeyMatters.update(auditKeyMatters => [newAuditKeyMatter, ...auditKeyMatters]);

    this.spinnerService.show();
    this.lutService.createAuditKeyMatter({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit key matter created successfully');
        this.auditKeyMatters.update(auditKeyMatters =>
          auditKeyMatters.map(akm => akm.id === tempId ? response : akm)
        );
      },
      error: (error) => {
        console.error('Error creating audit key matter:', error);
        this.spinnerService.hide();
        this.auditKeyMatters.update(auditKeyMatters =>
          auditKeyMatters.filter(akm => akm.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Key Matter:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditKeyMatter, matchedItems?: AuditKeyMatter[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAuditKeyMatterCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalAuditKeyMatters = this.auditKeyMatters();

        this.auditKeyMatters.update(auditKeyMatters =>
          auditKeyMatters.filter(akm => !bulkMergeRequest.leftIds.includes(akm.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeAuditKeyMattersBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit key matters merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadAuditKeyMatters();
          },
          error: (error) => {
            console.error('Error merging audit key matters:', error);
            this.spinnerService.hide();
            this.auditKeyMatters.set(originalAuditKeyMatters);
            this.rightItem = originalAuditKeyMatters.find(akm => akm.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditKeyMatters.filter(akm => bulkMergeRequest.leftIds.includes(akm.id));
            this.toast.error(`<strong>Failed to merge Audit Key Matters:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
